import React, { forwardRef, useId } from "react";

export interface InputProps
  extends Omit<React.InputHTMLAttributes<HTMLInputElement>, "size"> {
  label?: string;
  error?: string;
  helperText?: string;
  size?: "sm" | "md" | "lg";
  leftIcon?: React.ReactNode;
  rightIcon?: React.ReactNode;
  fullWidth?: boolean;
  variant?: "default" | "filled" | "ghost";
}

/**
 * Arc-style Input component
 * 
 * Features:
 * - Multiple variants: default, filled, ghost
 * - Multiple sizes: sm, md, lg
 * - Label and helper text support
 * - Error state
 * - Icon support (left and right)
 * - Smooth focus transitions
 */
const Input = forwardRef<HTMLInputElement, InputProps>(
  (
    {
      label,
      error,
      helperText,
      size = "md",
      leftIcon,
      rightIcon,
      fullWidth = true,
      variant = "default",
      className = "",
      disabled = false,
      required = false,
      id,
      ...props
    },
    ref
  ) => {
    const generatedId = useId();
    const inputId = id || generatedId;
    const hasError = !!error;

    // Size styles
    const sizeStyles = {
      sm: {
        input: "py-1.5 text-sm",
        paddingLeft: leftIcon ? "pl-9" : "pl-3",
        paddingRight: rightIcon ? "pr-9" : "pr-3",
        icon: "text-sm",
        iconLeft: "left-3",
        iconRight: "right-3",
        label: "text-xs mb-1.5",
      },
      md: {
        input: "py-2.5 text-sm",
        paddingLeft: leftIcon ? "pl-10" : "pl-4",
        paddingRight: rightIcon ? "pr-10" : "pr-4",
        icon: "text-base",
        iconLeft: "left-3.5",
        iconRight: "right-3.5",
        label: "text-sm mb-2",
      },
      lg: {
        input: "py-3.5 text-base",
        paddingLeft: leftIcon ? "pl-12" : "pl-5",
        paddingRight: rightIcon ? "pr-12" : "pr-5",
        icon: "text-lg",
        iconLeft: "left-4",
        iconRight: "right-4",
        label: "text-sm mb-2",
      },
    };

    const currentSize = sizeStyles[size];

    // Variant styles - using ring for focus with accent color
    const variantStyles = {
      default: `
        bg-surface-elevated
        border border-arc-border
        hover:border-arc-border-strong
        focus:border-transparent focus:ring-2 focus:ring-accent
      `,
      filled: `
        bg-surface-secondary
        border border-transparent
        hover:bg-surface-tertiary
        focus:bg-surface-elevated focus:ring-2 focus:ring-accent
      `,
      ghost: `
        bg-transparent
        border border-transparent
        hover:bg-surface-secondary
        focus:bg-surface-secondary focus:ring-2 focus:ring-accent
      `,
    };

    // Base input styles - Arc-style design
    const inputBaseStyles = `
      w-full
      rounded-arc-md
      font-medium
      text-content-primary
      placeholder:text-content-tertiary placeholder:font-normal
      transition-all duration-arc-normal ease-arc
      outline-none
      disabled:bg-surface-tertiary disabled:text-content-tertiary disabled:cursor-not-allowed disabled:border-arc-border
    `;

    const errorStyles = hasError
      ? "border-error hover:border-error focus:border-transparent focus:ring-2 focus:ring-error-main"
      : "";

    return (
      <div className={`${fullWidth ? "w-full" : ""} ${className}`}>
        {label && (
          <label
            htmlFor={inputId}
            className={`block font-semibold text-content-primary ${currentSize.label}`}
          >
            {label}
            {required && <span className="text-error ml-1">*</span>}
          </label>
        )}

        <div className="relative">
          {leftIcon && (
            <div
              className={`absolute ${currentSize.iconLeft} top-1/2 -translate-y-1/2 ${currentSize.icon} text-content-tertiary pointer-events-none`}
            >
              {leftIcon}
            </div>
          )}

          <input
            ref={ref}
            id={inputId}
            disabled={disabled}
            required={required}
            className={`
              ${inputBaseStyles}
              ${variantStyles[variant]}
              ${currentSize.input}
              ${currentSize.paddingLeft}
              ${currentSize.paddingRight}
              ${errorStyles}
            `}
            {...props}
          />

          {rightIcon && (
            <div
              className={`absolute ${currentSize.iconRight} top-1/2 -translate-y-1/2 ${currentSize.icon} text-content-tertiary`}
            >
              {rightIcon}
            </div>
          )}
        </div>

        {(error || helperText) && (
          <p
            className={`mt-1.5 text-xs ${
              hasError ? "text-error" : "text-content-tertiary"
            }`}
          >
            {error || helperText}
          </p>
        )}
      </div>
    );
  }
);

Input.displayName = "Input";

export default Input;
